import get from "lodash/get";
import { sendError } from "./sendError";
import { stripFriendbuyQueryParams } from "./url";

export interface ShopifyProductParams {
  productId?: string;
  productName?: string;
  productVariantId?: string;
  productImageUrl?: string;
  sku?: string;
  destinationUrl?: string;
}

export const getTargetWindow = () => {
  let targetWindow: any = window;
  let isCrossOrigin = false;

  try {
    if (window.parent && window !== window.top) {
      // Try to access a property to test for cross-origin (will throw if blocked)
      if (window.parent.location.href) {
        targetWindow = window.parent;
      }
    }
  } catch (e) {
    // Cross-origin security error
    isCrossOrigin = true;
    sendError({
      category: "widget",
      subCategory: "shopify",
      name: "getTargetWindow: Cross-origin access blocked",
      payload: JSON.stringify({ error: e.message, stack: e.stack }),
    });
  }

  return { targetWindow, isCrossOrigin };
};

/**
 * The variation the shopper actually chose. Shopify puts `?variant=` on the
 * product page URL when one is picked, and nothing there when none is.
 */
export const getSelectedVariantId = (targetWindow: any): string | null =>
  new URLSearchParams(targetWindow.location.search).get("variant");

export const extractProductParamsFromUrl = (): ShopifyProductParams => {
  const productParams: ShopifyProductParams = {};
  try {
    // Widget URLs use hash fragments, not query strings
    const hash = window.location.hash.slice(1); // Remove leading #
    const urlParams = new URLSearchParams(hash);
    const fbProductId = urlParams.get("fbProductId");
    const fbProductVariantId = urlParams.get("fbProductVariantId");
    const fbSku = urlParams.get("fbSku");
    const fbProductName = urlParams.get("fbProductName");
    const fbProductImageUrl = urlParams.get("fbProductImageUrl");

    if (
      fbProductId ||
      fbProductVariantId ||
      fbSku ||
      fbProductName ||
      fbProductImageUrl
    ) {
      if (fbProductId) {
        productParams.productId = fbProductId;
      }
      if (fbProductVariantId) {
        productParams.productVariantId = fbProductVariantId;
      }
      if (fbSku) {
        productParams.sku = fbSku;
      }
      if (fbProductName) {
        productParams.productName = fbProductName;
      }
      if (fbProductImageUrl) {
        productParams.productImageUrl = fbProductImageUrl;
      }
    }
  } catch (e) {
    sendError({
      category: "widget",
      subCategory: "shopify",
      name: "extractProductParamsFromUrl: Failed to parse URL hash params",
      payload: JSON.stringify({ error: e.message, stack: e.stack }),
    });
  }

  return productParams;
};

const extractFromProduct = (
  product: any,
  selectedVariantId: string | null
): ShopifyProductParams => {
  const params: ShopifyProductParams = {};
  if (!product) {
    return params;
  }

  const productId = get(product, "id");
  if (productId) {
    params.productId = String(productId);
  }

  const productName = get(product, "name");
  if (productName) {
    params.productName = productName;
  }

  // With no choice made the share is of the product, so no variation is
  // referenced at all rather than whichever one the page happened to show.
  const variants = get(product, "variants");
  const variant =
    selectedVariantId && Array.isArray(variants)
      ? variants.find((v: any) => String(v.id) === String(selectedVariantId))
      : undefined;

  if (variant) {
    const productVariantId = get(variant, "id");
    if (productVariantId) {
      params.productVariantId = String(productVariantId);
    }
    const sku = get(variant, "sku");
    if (sku) {
      params.sku = sku;
    }
  }

  return params;
};

const extractFromGlobals = (
  targetWindow: any,
  isCrossOrigin: boolean
): ShopifyProductParams => {
  const params: ShopifyProductParams = {};
  if (isCrossOrigin) {
    return params;
  }

  const saProduct = get(targetWindow, "ShopifyAnalytics.meta.product");
  const metaProduct = get(targetWindow, "meta.product");
  Object.assign(
    params,
    extractFromProduct(
      saProduct || metaProduct,
      getSelectedVariantId(targetWindow)
    )
  );

  return params;
};

export const extractShopifyParams = (
  currentUrl?: string
): ShopifyProductParams => {
  const productParams: ShopifyProductParams = {};
  try {
    const urlParams = extractProductParamsFromUrl();
    if (Object.keys(urlParams).length > 0) {
      urlParams.destinationUrl = stripFriendbuyQueryParams(
        currentUrl || window.location.href
      );

      return urlParams;
    }

    const { targetWindow, isCrossOrigin } = getTargetWindow();
    Object.assign(
      productParams,
      extractFromGlobals(targetWindow, isCrossOrigin)
    );

    productParams.destinationUrl = stripFriendbuyQueryParams(
      currentUrl || window.location.href
    );
  } catch (e) {
    sendError({
      category: "widget",
      subCategory: "shopify",
      name: "extractShopifyParams: Failed to extract product params",
      payload: JSON.stringify({ error: e.message, stack: e.stack }),
    });
  }

  return productParams;
};
